import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import useAuth from '@/hooks/useAuth';
import useProfileComplete from '@/hooks/useProfileComplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Upload, RefreshCw, Loader2, Check, X, AlertCircle } from 'lucide-react';
import { z } from 'zod';
import { profileCache } from '@/lib/profileCache';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const profileSchema = z.object({
  username: z.string()
    .trim()
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username must be less than 30 characters')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores, and hyphens'),
  name: z.string().trim().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
});

const AVATAR_STYLES = ['avataaars', 'bottts', 'lorelei', 'personas', 'pixel-art'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const MIN_AGE = 13;

const calculateAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const ProfileSetup = () => {
  const { user } = useAuth();
  const { refetch } = useProfileComplete();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [randomAvatars, setRandomAvatars] = useState<string[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');

  // Generate years (current year - 100 to current year - MIN_AGE)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let year = currentYear - MIN_AGE; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  }, []);

  // Generate days based on selected month/year
  const days = useMemo(() => {
    if (!birthMonth || !birthYear) {
      return Array.from({ length: 31 }, (_, i) => i + 1);
    }
    const daysInMonth = new Date(parseInt(birthYear), parseInt(birthMonth), 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [birthMonth, birthYear]);

  // Calculate age and check if valid
  const ageValidation = useMemo(() => {
    if (!birthDay || !birthMonth || !birthYear) {
      return { isValid: null, age: null };
    }
    const birthDate = new Date(parseInt(birthYear), parseInt(birthMonth) - 1, parseInt(birthDay));
    const age = calculateAge(birthDate);
    return { isValid: age >= MIN_AGE, age };
  }, [birthDay, birthMonth, birthYear]);

  const generateRandomAvatars = () => {
    const avatars = Array.from({ length: 6 }, () => {
      const style = AVATAR_STYLES[Math.floor(Math.random() * AVATAR_STYLES.length)];
      const seed = crypto.randomUUID();
      return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}`;
    });
    setRandomAvatars(avatars);
  };

  // Generate random avatars on mount
  useEffect(() => {
    generateRandomAvatars();
  }, []);

  // Check username availability with debounce
  useEffect(() => {
    const checkUsernameAvailability = async () => {
      if (username.length < 3) {
        setUsernameAvailable(null);
        return;
      }

      // Validate format
      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        setUsernameAvailable(false);
        return;
      }

      setCheckingUsername(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id')
          .eq('username', username.toLowerCase())
          .maybeSingle();

        setUsernameAvailable(!data && !error);
      } catch (error) {
        console.error('Username check error:', error);
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    };

    const timeoutId = setTimeout(checkUsernameAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [username]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (JPG, PNG, etc.)',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be less than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user?.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAvatarUrl(publicUrl);
      setUploadedFile(file);
      setSelectedAvatar(''); // Clear random avatar selection
      toast({
        title: 'Image uploaded',
        description: 'Your profile picture has been uploaded successfully',
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRandomAvatarSelect = (avatar: string) => {
    setSelectedAvatar(avatar);
    setAvatarUrl(avatar);
    setUploadedFile(null); // Clear uploaded file
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate username and name
    const validation = profileSchema.safeParse({ username, name });
    if (!validation.success) {
      toast({
        title: 'Validation error',
        description: validation.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    // Validate password
    if (password.length < 8) {
      toast({
        title: 'Invalid password',
        description: 'Password must be at least 8 characters',
        variant: 'destructive',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are the same',
        variant: 'destructive',
      });
      return;
    }

    // Validate date of birth
    if (!birthDay || !birthMonth || !birthYear) {
      toast({
        title: 'Date of birth required',
        description: 'Please enter your date of birth',
        variant: 'destructive',
      });
      return;
    }

    if (!ageValidation.isValid) {
      toast({
        title: 'Age requirement not met',
        description: 'You must be at least 13 years old to use imata',
        variant: 'destructive',
      });
      return;
    }

    // Check username availability
    if (!usernameAvailable) {
      toast({
        title: 'Username unavailable',
        description: 'This username is already taken. Please choose another one.',
        variant: 'destructive',
      });
      return;
    }

    // Ensure avatar is selected
    if (!avatarUrl) {
      toast({
        title: 'Avatar required',
        description: 'Please upload an image or select a random avatar',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // First, set the password in Supabase Auth
      const { error: passwordError } = await supabase.auth.updateUser({
        password: password,
      });

      if (passwordError) throw passwordError;

      // Get user email from auth
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userEmail = authUser?.email || null;

      // Format date of birth
      const dateOfBirth = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;

      // Then update the users table
      const { error } = await supabase
        .from('users')
        .update({
          username: username.trim().toLowerCase(),
          name: name.trim(),
          avatar_url: avatarUrl,
          username_last_changed_at: new Date().toISOString(),
          name_last_changed_at: new Date().toISOString(),
          has_password: true,
          email: userEmail,
          date_of_birth: dateOfBirth,
        })
        .eq('id', user?.id);

      if (error) throw error;

      // Cache the profile
      profileCache.set({
        id: user!.id,
        username: username.trim().toLowerCase(),
        name: name.trim(),
        avatar_url: avatarUrl,
      });

      toast({
        title: 'Profile created',
        description: 'Your profile and password have been set up successfully',
      });

      // Wait for profile completion status to update
      const isComplete = await refetch();
      
      if (isComplete) {
        navigate('/map');
      } else {
        throw new Error('Profile update verification failed');
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      
      // Check for specific password-related errors from Supabase Auth
      let errorMessage = 'Failed to update profile. Please try again.';
      if (error?.message) {
        if (error.message.toLowerCase().includes('same') || 
            error.message.toLowerCase().includes('different')) {
          errorMessage = 'New password must be different from your current password.';
        } else if (error.message.toLowerCase().includes('weak') || 
                   error.message.toLowerCase().includes('short')) {
          errorMessage = 'Password is too weak. Please choose a stronger password.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: 'Update failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md md:max-w-2xl lg:max-w-4xl">
        <CardHeader>
          <CardTitle>Complete Your Profile</CardTitle>
          <CardDescription>
            Tell us a bit about yourself to get started with imata
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Input */}
            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <div className="relative">
                <Input
                  id="username"
                  type="text"
                  placeholder="Choose a unique username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  maxLength={30}
                  required
                  className="pr-10"
                />
                {checkingUsername && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {!checkingUsername && username.length >= 3 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameAvailable ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-destructive" />
                    )}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                3-30 characters. Letters, numbers, underscores, and hyphens only. Can be changed once per year.
              </p>
            </div>

            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="name">Display Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your display name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
              />
              <p className="text-xs text-muted-foreground">
                Can be changed once per year after initial setup.
              </p>
            </div>

            {/* Date of Birth Input */}
            <div className="space-y-2">
              <Label>Date of Birth *</Label>
              <div className="grid grid-cols-3 gap-2">
                <Select value={birthDay} onValueChange={setBirthDay}>
                  <SelectTrigger>
                    <SelectValue placeholder="Day" />
                  </SelectTrigger>
                  <SelectContent>
                    {days.map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={birthMonth} onValueChange={setBirthMonth}>
                  <SelectTrigger>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, index) => (
                      <SelectItem key={month} value={(index + 1).toString()}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={birthYear} onValueChange={setBirthYear}>
                  <SelectTrigger>
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {ageValidation.isValid === false && (
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>You must be at least 13 years old to use imata</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                We require this to ensure you meet our{' '}
                <Link to="/safety" className="text-primary hover:underline">
                  age requirements
                </Link>
                . Your date of birth is never shared with other users.
              </p>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <Label htmlFor="password">Create Password *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
              <p className="text-xs text-muted-foreground">
                At least 8 characters. This will be used for future logins.
              </p>
            </div>

            {/* Confirm Password Input */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>

            {/* Avatar Upload */}
            <div className="space-y-2">
              <Label>Profile Picture *</Label>
              <div className="space-y-4">
                {/* Upload Section */}
                <div>
                  <Label htmlFor="avatar-upload" className="cursor-pointer">
                    <div className="border-2 border-dashed border-border rounded-lg p-6 hover:border-primary transition-colors flex flex-col items-center gap-2">
                      {uploading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      ) : uploadedFile ? (
                        <>
                          <img
                            src={avatarUrl}
                            alt="Uploaded avatar"
                            className="w-24 h-24 rounded-full object-cover"
                          />
                          <p className="text-sm text-muted-foreground">
                            Click to change image
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Upload your own image (Max 5MB)
                          </p>
                        </>
                      )}
                    </div>
                  </Label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or choose a random avatar
                    </span>
                  </div>
                </div>

                {/* Random Avatars */}
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    {randomAvatars.map((avatar, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleRandomAvatarSelect(avatar)}
                        className={`relative rounded-lg overflow-hidden aspect-square border-2 transition-all hover:scale-105 ${
                          selectedAvatar === avatar
                            ? 'border-primary ring-2 ring-primary/50'
                            : 'border-border'
                        }`}
                      >
                        <img
                          src={avatar}
                          alt={`Random avatar ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateRandomAvatars}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Generate New Avatars
                  </Button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !username.trim() || !name.trim() || !avatarUrl || !usernameAvailable || checkingUsername || !ageValidation.isValid}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileSetup;
